import { MessageCircleQuestionIcon } from "lucide-react";
import { format } from "date-fns";
import { CreatorAction } from "@/types/types";
import { Button } from "@/components/ui/button";
import { useAmyDeepLink } from "@/hooks/use-amy-deep-link";

/**
 * Amy-v2 attention task: informational flags Amy raised — a stalled
 * wait-gate hold or a creator who exhausted the nudge budget. Resolution is
 * conversational (release the hold, decide the next step), so the action is
 * a deep link into the Amy chat.
 */
export default function EveAttentionAction({ action }: { action: CreatorAction }) {
  const openAmy = useAmyDeepLink();
  const label = action.phase?.label ?? action.phase?.key ?? "phase";
  const isStall = action.attention_reason === "gate-stall";
  const since = action.stall_announced_at
    ? format(new Date(action.stall_announced_at), "MMM d, yyyy")
    : null;

  return (
    <div className="flex flex-col items-start gap-4 animate-in fade-in-0 slide-in-from-top-2 duration-250">
      {/* The header badge names the task; this says which phase and why. */}
      <p className="text-sm text-muted-foreground">
        <span className="font-medium text-foreground">
          {isStall ? `Stalled in the ${label} hold.` : `Out of nudges in ${label}.`}
        </span>{" "}
        {action.message ??
          (isStall
            ? `@${action.username} has been waiting on an external event that never arrived.`
            : `@${action.username} went quiet and Amy has used the nudge budget.`)}
        {isStall && since && ` Flagged ${since}.`}
        {!isStall &&
          action.nudge_count != null &&
          action.max_nudges != null &&
          ` (${action.nudge_count}/${action.max_nudges} nudges sent.)`}
      </p>

      <Button
        variant="default"
        size="sm"
        onClick={() =>
          openAmy(
            isStall
              ? `Tell me about the stalled "${label}" hold for @${action.username} on campaign "${action.campaign_name}" (campaign_creator_id ${action._id}, campaign_id ${action.campaign_id}) — should we release it or follow up?`
              : `@${action.username} on campaign "${action.campaign_name}" (campaign_creator_id ${action._id}, campaign_id ${action.campaign_id}) stopped replying in the ${label} phase and the nudges ran out — what are our options?`
          )
        }
      >
        <MessageCircleQuestionIcon /> Ask Amy
      </Button>
    </div>
  );
}
